import type { ProductEntity, SprintProductChapter } from '@data/utils/types';

import { rateIn, type VersionTeam } from '../../products/productDetail/budget';
import { effectiveSeniority } from '../../products/productMock';

export type PricingGapWorker = { id: string; name: string };

/** A price the matrix lacks: a chapter at a level somebody on the sprint is at. */
export type MissingRate = { chapterKey: string; level: string };

/** Who worked the sprint and cannot be priced: no seniority, or a seniority with no rate in the cost matrix. */
export type PricingGaps = {
  unlevelled: PricingGapWorker[];
  unrated: PricingGapWorker[];
  /** At cost only: no hourly cost on their member page, which is where it is filled in, not the rate card. */
  uncosted: PricingGapWorker[];
  /** The cells to fill, once each. */
  missingRates: MissingRate[];
  /** Every chapter somebody on the sprint works under, so the matrix need show no other. */
  chapters: string[];
  /** Everybody with hours on the sprint: the only people whose level or rate reaches the mail. */
  workers: string[];
};

/** Read as pricing reads it, version team first; only people with hours are asked about, once each. */
export const pricingGaps = (
  groups: SprintProductChapter[],
  team: VersionTeam | null,
  product: ProductEntity | undefined,
): PricingGaps => {
  const seen = new Set<string>();
  const gaps: PricingGaps = { unlevelled: [], unrated: [], uncosted: [], missingRates: [], chapters: [], workers: [] };
  groups.forEach(group =>
    group.members.forEach(({ member, totalMinutes }) => {
      if (totalMinutes <= 0 || seen.has(member.id)) return;
      seen.add(member.id);
      gaps.workers.push(member.id);
      const priced = team?.people.find(person => person.id === member.id);
      const onProduct = product?.team?.find(person => person.membershipId === member.id);
      const level = priced ? priced.level : effectiveSeniority(undefined, onProduct?.seniority ?? '');
      const name =
        priced?.name ||
        [member.user?.firstName, member.user?.lastName].filter(Boolean).join(' ') ||
        member.user?.email ||
        '';
      const chapterKey = priced?.chapterKey || onProduct?.chapterKey || '';
      if (chapterKey && !gaps.chapters.includes(chapterKey)) gaps.chapters.push(chapterKey);
      // A fixed price needs nothing from anyone; at cost needs their hourly cost, not a level or a cell.
      if (team?.pricing?.mode === 'fixedPrice') return;
      if (team?.pricing?.mode === 'atCost') {
        if (rateIn(team, member.id) === null) gaps.uncosted.push({ id: member.id, name });
      } else if (!level) {
        gaps.unlevelled.push({ id: member.id, name });
      } else if (rateIn(team, member.id) === null) {
        gaps.unrated.push({ id: member.id, name });
        const known = gaps.missingRates.some(cell => cell.chapterKey === chapterKey && cell.level === level);
        if (chapterKey && !known) gaps.missingRates.push({ chapterKey, level });
      }
    }),
  );
  return gaps;
};
